"use client";

import { useCallback, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { formatRegionalTime } from "@/lib/i18n/config/languages";
import { cn } from "@/lib/utils";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useTranslation } from "@/hooks/use-translation";
import {
  clearErrorLog,
  getErrorLogEntries,
  getServerSnapshotErrorLog,
  serializeErrorLogEntries,
  subscribeErrorLog,
  type ErrorLogEntry,
} from "../stores/error-log-store";

function shortSource(source: string | undefined): string {
  if (!source) return "";
  // Strip origin prefix
  try {
    const url = new URL(source);
    return url.pathname;
  } catch {
    return source;
  }
}

function StackTrace({ stack }: { stack: string }) {
  return (
    <pre className="mt-1 text-sm font-mono text-muted-foreground/70 whitespace-pre-wrap break-all leading-relaxed bg-muted/30 rounded p-2 max-h-32 overflow-y-auto">
      {stack}
    </pre>
  );
}

function ErrorRow({
  entry,
  formatLocale,
  timeZone,
}: {
  entry: ErrorLogEntry;
  formatLocale: string | null;
  timeZone: string | null;
}) {
  const t = useTranslations("debug.errors");
  const [expanded, setExpanded] = useState(false);
  const kindColor =
    entry.kind === "promise" ? "text-orange-500" : "text-red-500";
  const kindBg =
    entry.kind === "promise" ? "bg-orange-500/10" : "bg-red-500/10";

  return (
    <div className="py-1.5 px-2 rounded hover:bg-muted/20 text-sm group">
      <div
        className="flex items-start gap-2 cursor-pointer"
        onClick={() => setExpanded((p) => !p)}
      >
        <span className="text-muted-foreground/60 w-16 shrink-0 text-sm pt-px">
          {formatRegionalTime(entry.timestamp, formatLocale, timeZone)}
        </span>
        <span
          className={cn(
            "shrink-0 px-1.5 py-0.5 rounded text-sm font-mono",
            kindBg,
            kindColor,
          )}
        >
          {entry.kind === "promise" ? t("promiseKind") : t("jsKind")}
        </span>
        <span className="flex-1 text-foreground/80 break-all leading-relaxed">
          {entry.message}
        </span>
        {(entry.occurrences ?? 1) > 1 && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-sm text-muted-foreground">
            {"×"}
            {entry.occurrences}
          </span>
        )}
        {entry.stack && (
          <span className="text-muted-foreground/40 text-sm shrink-0 pt-px">
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>
      {(entry.source ?? entry.lineno) && (
        <div className="mt-0.5 ml-[72px] text-sm text-muted-foreground/60 font-mono">
          {shortSource(entry.source)}
          {entry.lineno !== undefined && `:${entry.lineno}`}
          {entry.colno !== undefined && `:${entry.colno}`}
        </div>
      )}
      {expanded && entry.stack && <StackTrace stack={entry.stack} />}
    </div>
  );
}

export function ErrorLogPanel() {
  const { formatLocale, timeZone } = useTranslation("common");
  const t = useTranslations("debug.errors");
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  const entries = useSyncExternalStore(
    subscribeErrorLog,
    getErrorLogEntries,
    getServerSnapshotErrorLog,
  );

  const handleClear = useCallback(() => {
    clearErrorLog();
  }, []);

  const handleCopyJson = () => {
    copyToClipboard(serializeErrorLogEntries(entries));
  };

  if (entries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        {t("empty")}
      </div>
    );
  }

  const errorCount = entries.reduce(
    (count, entry) =>
      entry.kind === "js-error" ? count + (entry.occurrences ?? 1) : count,
    0,
  );
  const promiseCount = entries.reduce(
    (count, entry) =>
      entry.kind === "promise" ? count + (entry.occurrences ?? 1) : count,
    0,
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {errorCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">
              {t("jsErrorCount", { count: errorCount })}
            </span>
          )}
          {promiseCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-500">
              {t("promiseErrorCount", { count: promiseCount })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleCopyJson}
            className="cursor-pointer rounded border border-border bg-primary/10 px-2 py-0.5 font-mono text-sm text-primary transition-colors hover:bg-primary/20"
          >
            <span aria-live="polite">
              {isCopied ? t("copied") : t("copyJson")}
            </span>
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            {t("clear")}
          </button>
        </div>
      </div>
      <div className="space-y-0.5">
        {entries.map((entry) => (
          <ErrorRow
            key={entry.id}
            entry={entry}
            formatLocale={formatLocale}
            timeZone={timeZone}
          />
        ))}
      </div>
    </div>
  );
}
